import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Steps } from '@/components/ui/steps'
import { useToast } from '@/components/ui/toast'
import api from '@/lib/api'
import { formatBRL, formatCnpj, formatDate } from '@/lib/utils'
import { ArrowRight, Download } from 'lucide-react'
import { Fragment, useEffect, useState } from 'react'
import { periodoInicial, periodoValido } from './nfPeriodo'

const STEPS = [
  { key: 'periodo', label: 'Período' },
  { key: 'selecionar', label: 'Selecionar NF’s' },
]

export default function BaixarNfsModal({ open, onClose }) {
  const toast = useToast()
  const [periodo, setPeriodo] = useState(periodoInicial())
  const [step, setStep] = useState('periodo')
  const [preview, setPreview] = useState([])
  const [marcados, setMarcados] = useState({})
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (open) {
      setPeriodo(periodoInicial())
      setStep('periodo')
      setPreview([])
      setMarcados({})
    }
  }, [open])

  async function carregarPreview() {
    if (!periodoValido(periodo)) {
      toast.error('Informe um período válido.')
      return
    }
    setLoading(true)
    try {
      const { data } = await api.get('/nfs/downloads/preview', { params: periodo })
      setPreview(data.data || [])
      setMarcados(Object.fromEntries((data.data || []).map((nf) => [nf.nfID, true])))
      setStep('selecionar')
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Não foi possível carregar as NFs do período.')
    } finally {
      setLoading(false)
    }
  }

  async function baixar() {
    const nfIDs = preview.filter((nf) => marcados[nf.nfID]).map((nf) => nf.nfID)
    if (nfIDs.length === 0) {
      toast.error('Selecione ao menos uma NF.')
      return
    }
    setLoading(true)
    try {
      const { data } = await api.post('/nfs/downloads', { nfIDs }, { responseType: 'blob' })
      const url = URL.createObjectURL(data)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = `NFs-${periodo.inicio}-${periodo.fim}.zip`
      anchor.click()
      URL.revokeObjectURL(url)
      toast.success(`${nfIDs.length} PDF(s) compactado(s) com sucesso.`)
      onClose()
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Não foi possível gerar o ZIP das NFs.')
    } finally {
      setLoading(false)
    }
  }

  const selecionadas = preview.filter((nf) => marcados[nf.nfID])
  const todasMarcadas = preview.length > 0 && selecionadas.length === preview.length
  function alternarTodas() {
    const novoValor = !todasMarcadas
    setMarcados(Object.fromEntries(preview.map((nf) => [nf.nfID, novoValor])))
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Baixar NFs em lote"
      description="Selecione um período e baixe os PDFs das NFs emitidas em um único arquivo ZIP."
      size={step === 'periodo' ? 'md' : 'xl'}
      footer={step === 'periodo' ? (
        <>
          <Button variant="outline" onClick={onClose}>Fechar</Button>
          <Button onClick={carregarPreview} disabled={loading}>
            <ArrowRight className="h-4 w-4" />
            {loading ? 'Carregando...' : 'Continuar'}</Button>
        </>
      ) : (
        <>
          <Button variant="outline" onClick={() => setStep('periodo')}>Voltar</Button>
          <Button onClick={baixar} disabled={loading || selecionadas.length === 0}>
            <Download className="h-4 w-4" />
            {loading ? 'Preparando ZIP...' : `Baixar ${selecionadas.length} NF(s)`}
          </Button>
        </>
      )}
    >
      <Steps steps={STEPS} current={step} />

      {step === 'periodo' ? (
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Data inicial</Label>
            <Input type="date" value={periodo.inicio} onChange={(e) => setPeriodo((p) => ({ ...p, inicio: e.target.value }))} />
          </div>
          <div className="space-y-1.5">
            <Label>Data final</Label>
            <Input type="date" value={periodo.fim} onChange={(e) => setPeriodo((p) => ({ ...p, fim: e.target.value }))} />
          </div>
        </div>
      ) : preview.length === 0 ? (
        <p className="py-4 text-sm text-muted-foreground">Nenhuma NF emitida com PDF foi encontrada no período.</p>
      ) : (
        <div className="rounded-lg border border-border overflow-hidden">
          <div className="max-h-[55vh] overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="w-10 px-3 py-2">
                    <input type="checkbox" checked={todasMarcadas} onChange={alternarTodas} title={todasMarcadas ? 'Desmarcar todas' : 'Marcar todas'} className="h-4 w-4 accent-[var(--primary)]" />
                  </th>
                  <th className="px-3 py-2 font-medium">Cliente</th>
                  <th className="px-3 py-2 font-medium">CNPJ</th>
                  <th className="px-3 py-2 font-medium">Emissão</th>
                  <th className="px-3 py-2 font-medium">Contrato</th>
                  <th className="px-3 py-2 font-medium text-right">Valor</th>
                </tr>
              </thead>
              <tbody>
                {preview.map((nf) => (
                  <Fragment key={nf.nfID}>
                    <tr className="border-t border-border/60">
                      <td className="px-3 pt-2 align-top">
                        <input type="checkbox" checked={!!marcados[nf.nfID]} onChange={(e) => setMarcados((m) => ({ ...m, [nf.nfID]: e.target.checked }))} className="h-4 w-4 accent-[var(--primary)]" />
                      </td>
                      <td className="px-3 pt-2 align-top font-medium">{nf.clienteNome}</td>
                      <td className="px-3 pt-2 align-top whitespace-nowrap text-muted-foreground">{formatCnpj(nf.clienteCnpj)}</td>
                      <td className="px-3 pt-2 align-top whitespace-nowrap text-muted-foreground">{formatDate(nf.dataEmissao)}</td>
                      <td className="px-3 pt-2 align-top whitespace-nowrap text-muted-foreground">{nf.numeroContrato}</td>
                      <td className="px-3 pt-2 align-top text-right whitespace-nowrap tabular-nums">{formatBRL(nf.valor)}</td>
                    </tr>
                    <tr>
                      <td />
                      <td colSpan={5} className="px-3 pb-2 pt-0.5 text-xs text-muted-foreground/80 whitespace-pre-line break-words">{nf.descricaoServico}</td>
                    </tr>
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <div className="border-t border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
            {selecionadas.length} de {preview.length} selecionada(s)
          </div>
        </div>
      )}
    </Dialog>
  )
}
